import styles from './config.module.css'

import type { CellContext } from '@tanstack/react-table'
import React from 'react'

import {
  defaultColumnSorting,
  SEARCH_APPEARS_ON_COLUMN_FIELD,
  SEARCH_INDICATORS_COLUMN_FIELD,
  SEARCH_SOURCE_COLUMN_FIELD,
  SEARCH_TITLE_COLUMN_FIELD,
} from '@disco/constants/search'

import type { Track } from 'types/track'

import { Text } from 'components/ds'
import { sanitizeColumnId } from 'components/ds/DataTable/table-utils'
import type { ColumnTypeConfig } from 'components/search/search-columns/types'

import { AppearsOnColumn } from './appears-on-column'
import { AppearsOnHeader } from './appears-on-column/appears-on-header'
import { DurationColumn } from './duration-column'
import {
  IndicatorsColumn,
  INDICATORS_COLUMN_DEFAULT_SIZE,
} from './indicators-column'
import { IndicatorsHeader } from './indicators-column/components/indicators-header'
import { SourceColumn } from './source-column'
import { SourceColumnFilter } from './source-column/source-column-filter'
import { TagsColumn } from './tags-column'
import { TrackTitleColumn } from './track-title-column'
import { WaveformColumn } from './waveform-column'

const renderCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value)
  }
  return ''
}

const DefaultColumnCell = React.memo(
  ({ info }: { info: CellContext<Track, unknown> }) => {
    const colId = sanitizeColumnId(String(info.column.id))
    return (
      <Text
        className={styles.cellText}
        // --col-*-size is injected per-render by TanStack Table; not a static class.
        style={{
          width: `calc(var(--col-${colId}-size) * 1px - 20px)`,
        }}
      >
        {renderCellValue(info.getValue())}
      </Text>
    )
  },
)

DefaultColumnCell.displayName = 'DefaultColumnCell'

export { defaultColumnSorting }

export const defaultColumnConfig: ColumnTypeConfig<Track> = {
  matcher: () => true,
  cell: (_, info) => <DefaultColumnCell info={info} />,
  minSize: 100,
  size: 150,
  enableResizing: true,
}

export const getColumnTypes = (
  mdsEnabled: boolean,
): ColumnTypeConfig<Track>[] => [
  {
    id: SEARCH_TITLE_COLUMN_FIELD,
    header: () => 'Title',
    matcher: metadataDef => metadataDef.field === SEARCH_TITLE_COLUMN_FIELD,
    cell: (metadataDef, info) => (
      <TrackTitleColumn metadataDef={metadataDef} info={info} />
    ),
    enableSorting: true,
    enableHiding: false, // Title must always be displayed.
    minSize: 100,
    size: 300,
    enableResizing: true,
  },

  {
    accessorKey: () => 'duration',
    matcher: metadataDef => metadataDef.field === 'Duration',
    cell: (metadataDef, info) => (
      <DurationColumn metadataDef={metadataDef} info={info} />
    ),
    enableSorting: false,
    size: 50,
    minSize: 50,
    enableResizing: true,
  },

  {
    isDisplayColumn: true,
    matcher: metadataDef => metadataDef.field === 'Waveform',
    cell: (metadataDef, info) => (
      <WaveformColumn metadataDef={metadataDef} info={info} />
    ),
    enableSorting: false,
    minSize: 100,
    size: 400,
    enableResizing: true,
  },

  {
    accessorKey: () => 'native_tags',
    matcher: metadataDef => metadataDef.field === 'NativeTags',
    cell: (metadataDef, info) => (
      <TagsColumn metadataDef={metadataDef} info={info} />
    ),
    enableSorting: false,
    minSize: 100,
    size: 300,
    enableResizing: true,
  },

  {
    id: SEARCH_SOURCE_COLUMN_FIELD,
    header: () => <SourceColumnFilter />,
    accessorFn: row => row.scope,
    matcher: metadataDef => metadataDef.field === SEARCH_SOURCE_COLUMN_FIELD,
    cell: (_metadataDef, info) => <SourceColumn info={info} />,
    enableSorting: false,
    enableHiding: !mdsEnabled,
    minSize: 120,
    size: 180,
    enableResizing: true,
  },

  {
    id: SEARCH_INDICATORS_COLUMN_FIELD,
    isDisplayColumn: true,
    header: () => <IndicatorsHeader />,
    matcher: metadataDef =>
      metadataDef.field === SEARCH_INDICATORS_COLUMN_FIELD,
    cell: (_metadataDef, info) => <IndicatorsColumn info={info} />,
    enableSorting: false,
    minSize: 80,
    // Shared with the cell's dot-reveal threshold, so a resting column shows
    // exactly the base dot cluster.
    size: INDICATORS_COLUMN_DEFAULT_SIZE,
    enableResizing: true,
  },

  {
    id: SEARCH_APPEARS_ON_COLUMN_FIELD,
    isDisplayColumn: true,
    header: () => <AppearsOnHeader />,
    matcher: metadataDef =>
      metadataDef.field === SEARCH_APPEARS_ON_COLUMN_FIELD,
    cell: (_metadataDef, info) => <AppearsOnColumn info={info} />,
    enableSorting: false,
    minSize: 100,
    size: 150,
    enableResizing: true,
  },
]
